<template>
  <div class="customization-field">
    <div class="full-width" :class="{ loading }">
      <label class="mb-0">
        <span class="field-label">{{ getFieldLabel(field) }}</span>
        <i
          class="fa-regular fa-circle-question info-tooltip ml-1"
          :data-tippy-content="field.description"
        ></i>
      </label>
      <transition name="fade">
        <div v-if="editing">
          <div class="d-flex flex-column">
            <div class="d-flex flex-column">
              <div v-if="fieldType == 'color'">
                <input
                  ref="inputRef"
                  v-model="color"
                  name="color"
                  type="text"
                  :placeholder="field.default"
                  :autofocus="'autofocus'"
                  class="color-text-input"
                />
                <input
                  v-model="color"
                  name="color"
                  type="color"
                  class="color-text-input"
                />
              </div>
              <div v-else-if="fieldType == 'image_url'">
                <AssetUpload
                  :asset="asset"
                  class="form-control"
                  :links="{
                    file_upload:
                      '/customizations/' + field.key + '/upload_asset'
                  }"
                  @upload-complete="($evt) => uploadComplete(field.key, $evt)"
                  @upload-canceled="uploadCanceled"
                />
              </div>
              <textarea
                v-else
                ref="inputRef"
                type="text"
                :placeholder="`Default: ${field.default}`"
                :value="field.value"
                class="form-control"
                :autofocus="'autofocus'"
              />

              <div class="d-flex button-group">
                <div class="button-item" @click="editing = false">
                  <i class="fa fa-times" />
                  <span style="font-size: 0.8em">Cancel Changes</span>
                </div>
                <div class="button-item" @click="handleReset">
                  <i class="fa fa-undo" />
                  <span style="font-size: 0.8em">Reset to Default</span>
                </div>
                <div class="button-item" @click="handleSubmit">
                  <i class="fa fa-save" />
                  <span style="font-size: 0.8em">Save Changes</span>
                </div>
              </div>
            </div>

            <div v-if="error" class="text-danger mt-1">
              {{ error }}
            </div>
          </div>
        </div>
        <div
          v-else
          class="d-flex editable-label align-items-center"
          @click="editing = true"
        >
          <div
            v-if="fieldType == 'color'"
            class="color-preview-swatch"
            :style="`background-color: ${field.value}`"
          ></div>
          <span class="truncated mr-2">{{
            field.value || field.default || "--"
          }}</span>
          <i class="fa-regular fa-pen-to-square link"></i>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import CONSTANTS from "@/Constants.js";
import AssetUpload from "./AssetUpload.vue";
import { CustomizationRecord } from "./customizations";
import { injectUseTenantCustomization } from "./useTenantCustomization";

const {
  ALLOWED_FILE_EXTENSIONS_ICON,
  ALLOWED_FILE_EXTENSIONS_LOGO,
  AWS_S3_CDN_ROOT,
  CDN_ROOT,
  CDN_ROOT_TEMPLATE
} = CONSTANTS;

const props = defineProps({
  field: {
    type: Object as () => CustomizationRecord,
    default: () => ({})
  }
});

const store = useStore();

const { handleChange, handleReset: resetCustomization } =
  injectUseTenantCustomization();
const asset = ref({
  name: props.field.description,
  extensions: getValidExtensions(props.field.key.includes("icon_url"))
});
const color = ref(props.field.value || props.field.default);
const editing = ref(false);
const tippyMounted = ref(false);
const loading = ref(false);
const error = ref("");
const inputRef = ref<HTMLInputElement>();

watch(editing, (newValue, oldValue) => {
  if (newValue !== oldValue && newValue && !tippyMounted.value) {
    // Mount field level tooltips on show
    window.tippy("[data-tippy-content]");
  }
});

onMounted(() => {
  window.tippy(".info-tooltip");
});

function getFieldLabel(field) {
  return field.key.replace(/_/g, " ");
}

async function handleSubmit() {
  const isImageUpload = props.field.data_type === "image_url";
  const value = isImageUpload ? props.field.value : inputRef.value!.value;
  const isIcon = props.field.key.includes("icon_url");
  const validExtensions = getValidExtensions(isIcon);
  const isValid = isValidImageSrc(validExtensions, value);

  // Need a better way to handle per field validation
  if (isImageUpload) {
    if (!value) {
      window.notify.error("Please upload a file, or specify a URL to use.");
      return;
    }
    if (!isValid) {
      handleInvalidExtension(validExtensions, isIcon);
      return;
    }
  }

  if (value) {
    loading.value = true;
    try {
      await handleChange(props.field, value);
      error.value = "";
      editing.value = false;
    } catch (err: any) {
      error.value = err;
    }
    loading.value = false;
  }
}

function getValidExtensions(isIcon) {
  return isIcon ? ALLOWED_FILE_EXTENSIONS_ICON : ALLOWED_FILE_EXTENSIONS_LOGO;
}

function isValidImageSrc(validExtensions, src) {
  if (!src) {
    return false;
  }
  return validExtensions.some((extension) => {
    return src.toLowerCase().endsWith(extension);
  });
}

function handleInvalidExtension(validExtensions, isIcon) {
  let extensionNameText = "";
  if (validExtensions.length > 1) {
    const lastExtension = validExtensions[validExtensions.length - 1];
    const otherExtensions = validExtensions.slice(
      0,
      validExtensions.length - 1
    );

    extensionNameText = otherExtensions.join(", ") + " and " + lastExtension;
  } else {
    extensionNameText = validExtensions[0];
  }

  let errorMessage =
    "Only " + extensionNameText + " extensions are allowed for ";

  if (isIcon) {
    errorMessage += "icons.";
  } else {
    errorMessage += "logos due to email client restrictions.";
  }

  window.notify.error(errorMessage);
}

async function handleReset() {
  confirmDelete(props.field).then(async () => {
    loading.value = true;
    try {
      await resetCustomization(props.field);
      editing.value = false;
    } catch (e) {
      // Do nothing
    }
    loading.value = false;
  });
}

async function confirmDelete(field) {
  const fieldLabel = getFieldLabel(field);
  const payload = {
    message: `Are you sure you want to reset <span class="titleize">${fieldLabel}</span>?`,
    title: `Confirm Customization Reset`
  };
  return store.dispatch("confirmDelete", payload);
}

function uploadCanceled() {
  props.field.value = props.field.default;
}

function uploadComplete(field_name, s3Key) {
  if (s3Key.startsWith("https://") || s3Key.startsWith("http://")) {
    props.field.value = s3Key;
    return;
  }
  const env = s3Key.substring(0, s3Key.indexOf("/"));
  if (env === "development") {
    props.field.value = AWS_S3_CDN_ROOT + s3Key;
  } else if (env === "production") {
    props.field.value = CDN_ROOT + s3Key.substring(env.length);
  } else {
    props.field.value =
      CDN_ROOT_TEMPLATE.replace(":env", env) + s3Key.substring(env.length);
  }
}

const fieldType = computed(() => {
  return props.field.data_type;
});
</script>

<style lang="scss" scoped>
label {
  color: #353a3b;
}
.field-label {
  text-transform: capitalize;
  font-weight: bold;
}
.customization-field {
  color: #5f636a;
}

.truncated {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.button-group {
  border: 1px solid var(--primary-color);
  border-radius: 0 0 5px 5px;
  overflow: hidden;
  .button-item {
    flex: 1;
    background-color: #fff;
    color: var(--primary-color);
    display: flex;
    justify-content: center;
    align-items: center;
    border-right: 1px solid var(--primary-color);
    padding: 5px 0;
    cursor: pointer;
    transition: all 250ms ease;
    &:hover {
      background-color: var(--primary-color);
      color: #fff;
    }
    &:last-of-type {
      border-right: none;
    }
    i {
      margin-right: 5px;
    }
  }
}

.editable-label {
  overflow: hidden;
  cursor: pointer;
  max-width: 900px;
  &:hover {
    color: var(--primary-color);
    span {
      text-decoration: underline;
    }
    .hint-text {
      opacity: 1;
    }
  }
  .hint-text {
    color: #ededed;
    opacity: 0;
    font-style: italic;
  }
  i {
    color: var(--primary-color);
  }
}
</style>
